/** item 4 · PWA 會議紀錄明細（公司會議室）。主席/紀錄人員/建立者可編輯（can_edit_minutes），其餘唯讀。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, IconButton, List, ListItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { useMessage } from '../../../contexts/MessageContext';
import {
  ActionItem, getMeeting, getMinutes, Minutes, updateMinutes,
} from '../officeMeeting/services/officeMeetingService';

const OfficeMinutesDetailPage: React.FC = () => {
  const { id } = useParams();
  const mid = Number(id);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pwaMinutes.${k}`, d);
  const { showApiError, showSuccess } = useMessage();

  const [mn, setMn] = useState<Minutes | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [roles, setRoles] = useState<{ chair?: string | null; recorder?: string | null }>({});
  const [edit, setEdit] = useState(false);
  const [summary, setSummary] = useState('');
  const [decisions, setDecisions] = useState<{ content: string }[]>([]);
  const [items, setItems] = useState<ActionItem[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getMinutes(mid);
      setMn(data); setSummary(data.summary || '');
      setDecisions(data.decisions || []); setItems(data.action_items || []);
      const meeting = await getMeeting(mid);
      setCanEdit(!!meeting.can_edit_minutes);
      setRoles({ chair: meeting.chair_name, recorder: meeting.recorder_name });
    } catch (err) { showApiError(err); }
  }, [mid, showApiError]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateMinutes(mid, { summary, decisions, action_items: items });
      setMn(updated); setEdit(false);
      showSuccess('SYS020001', { name: tt('title', '會議紀錄') });
    } catch (err) { showApiError(err); } finally { setSaving(false); }
  };

  if (!mn) return <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{tt('loading', '載入中…')}</Typography></Box>;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Button size="small" onClick={() => nav('/ee_pwa_minutes')}>← {tt('backList', '返回清單')}</Button>
        {canEdit && !edit && <Button size="small" onClick={() => setEdit(true)}>{tt('edit', '編輯')}</Button>}
        {edit && <Button size="small" variant="contained" onClick={save} disabled={saving}>{tt('save', '儲存')}</Button>}
      </Stack>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>📝 {tt('title', '會議紀錄')}</Typography>
      {(roles.chair || roles.recorder) && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {roles.chair && <Chip size="small" label={`${tt('chair', '主席')}：${roles.chair}`} />}
          {roles.recorder && <Chip size="small" variant="outlined" label={`${tt('recorder', '紀錄人員')}：${roles.recorder}`} />}
        </Stack>
      )}

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('summary', '摘要')}</Typography>
        {edit
          ? <TextField fullWidth multiline minRows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
          : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{mn.summary || tt('none', '（無）')}</Typography>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('decisions', '決議')}</Typography>
        {edit ? (
          <Stack spacing={1}>
            {decisions.map((d, i) => (
              <Stack key={i} direction="row" spacing={1}>
                <TextField fullWidth size="small" value={d.content}
                  onChange={(e) => setDecisions(decisions.map((x, j) => j === i ? { content: e.target.value } : x))} />
                <IconButton size="small" onClick={() => setDecisions(decisions.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setDecisions([...decisions, { content: '' }])}>{tt('addDecision', '新增決議')}</Button>
          </Stack>
        ) : (mn.decisions.length === 0
          ? <Typography variant="body2" color="text.secondary">{tt('none', '（無）')}</Typography>
          : <List dense disablePadding>{mn.decisions.map((d, i) => <ListItem key={i} disableGutters>• {d.content}</ListItem>)}</List>)}
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('actions', '待辦事項')}</Typography>
        {edit ? (
          <Stack spacing={1}>
            {items.map((a, i) => (
              <Stack key={a.id || i} direction="row" spacing={1} alignItems="center">
                <TextField size="small" sx={{ flex: 1 }} label={tt('aiContent', '內容')} value={a.content}
                  onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, content: e.target.value } : x))} />
                <IconButton size="small" onClick={() => setItems(items.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => setItems([...items, { id: `new-${items.length}`, content: '', assignee_name: '', due: '', status: 'pending' }])}>
              {tt('addAction', '新增待辦')}</Button>
          </Stack>
        ) : (mn.action_items.length === 0
          ? <Typography variant="body2" color="text.secondary">{tt('none', '（無）')}</Typography>
          : <List dense disablePadding>{mn.action_items.map((a) => (
            <ListItem key={a.id} disableGutters sx={{ display: 'block' }}>
              <Typography variant="body2">• {a.content}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                {a.assignee_name && <Chip size="small" color={a.assignee_user_id ? 'info' : 'default'} label={a.assignee_name} />}
                {a.due && <Chip size="small" variant="outlined" label={a.due} />}
              </Stack>
            </ListItem>))}</List>)}
      </Paper>
    </Box>
  );
};

export default OfficeMinutesDetailPage;
